// ── שמות מחלקה, שלב וסיבת אי-התאמה — בשפת העמדה (סקר העמדה 11/10/2026 #9) ──
//
// נמדד ברוסית: שבע סיבות האי-התאמה, שמות המחלקות והשלבים נשארו בעברית.
// · מחלקה — `nameRu` מהמחלקה (`deptName` ב-api/station), כמו בפס „המחלקה שלי".
// · שלב — שם השלב הוא לרוב שם המחלקה („ריתוך", „הכנות \ גמר"); כשהוא זהה לשם
//   של מחלקה — השם הרוסי שלה. שלב בשם אחר נשאר כפי שהמהנדס כתב: הוא נתון.
// · סיבת אי-התאמה — אין `nameRu` בטבלה; שש ברירות המחדל של השרת
//   (`DEFAULT_NC_CATEGORIES`) והפסול מתורגמות כאן. סיבה שמנהל הוסיף — כמו שנכתבה.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deptName, stationApi, type StationDepartment } from '../api/station.js';
import { useI18n } from '../i18n/index.js';

const NC_RU: Record<string, string> = {
  'חוסר חומר': 'Нехватка материала',
  'אי-התאמת ייצור': 'Несоответствие производства',
  'אי-התאמה בתחנה קודמת': 'Несоответствие на предыдущем участке',
  'חסר שרטוט': 'Нет чертежа',
  'חסר רכש': 'Не закуплено',
  'ייצור לקוח': 'Производство заказчика',
  'יוצר לפי רוויזיה קודמת': 'Сделано по предыдущей ревизии',
  'פסול': 'Брак',
  // „התקבל מהלקוח" אוחדה לתוכה (מנכ״ל 03/11/2026) — שם אחד, תרגום אחד.
  'תלונת לקוח': 'Жалоба клиента',
};

// ── מחלקות משרד אינן עמדת עבודה (סקר העמדה 11/10, קוסמטי) ──────────────────
// „המחלקה שלי" וחלון הסיום הציעו את כל 10 המחלקות, כולל הנהלה והנדסה — אין בהן
// עבודה על חלק בעמדה. ⚠ מסננים בתצוגה בלבד; המחלקה הפעילה נשארת תמיד.
const OFFICE_TOKENS = ['הנהלה', 'הנדסה'];
export function isOfficeDepartment(name: string): boolean {
  const n = name.trim();
  return OFFICE_TOKENS.some((t) => n.includes(t));
}

export function ncCategoryName(name: string, lang: string): string {
  return lang === 'ru' ? NC_RU[name.trim()] ?? name : name;
}

/** שם שלב/מחלקה בשפת העמדה, לפי רשימת המחלקות. ללא התאמה — כמו שהוא. */
export function stageNameIn(name: string | null | undefined, departments: readonly StationDepartment[], lang: string): string {
  if (!name) return '';
  if (lang !== 'ru') return name;
  const n = name.trim();
  const d = departments.find((x) => x.name.trim() === n);
  return d ? deptName(d, lang) : name;
}

/** אותו דבר כ-hook — רשימת המחלקות מאותו מטמון של העמדה (`station-depts`). */
export function useStageLabel(): (name: string | null | undefined) => string {
  const { lang } = useI18n();
  const q = useQuery({ queryKey: ['station-depts'], queryFn: stationApi.departments, staleTime: 5 * 60_000, enabled: lang !== 'he' });
  const deps = q.data;
  return useMemo(() => (name: string | null | undefined) => stageNameIn(name, deps ?? [], lang), [deps, lang]);
}
